import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { SubEvent } from "../types/event";
import { DatePicker } from "./DatePicker";
import { Input } from "./inputs/Input";
import {
  CalendarDate,
  CalendarDateTime,
  parseDate,
  startOfMonth,
  endOfMonth,
  getDayOfWeek,
  today,
  getLocalTimeZone,
} from "@internationalized/date";

export interface SubEventManagerHandle {
  hasUncommittedData: () => boolean;
}

interface SubEventManagerProps {
  defaultValue?: SubEvent[] | null;
}

function generateId(): string {
  return crypto.randomUUID();
}

const SUB_EVENT_COLORS = [
  { bg: "bg-blue-200", text: "text-blue-800" },
  { bg: "bg-green-200", text: "text-green-800" },
  { bg: "bg-purple-200", text: "text-purple-800" },
  { bg: "bg-amber-200", text: "text-amber-800" },
  { bg: "bg-rose-200", text: "text-rose-800" },
  { bg: "bg-cyan-200", text: "text-cyan-800" },
];

function getColor(index: number) {
  return SUB_EVENT_COLORS[index % SUB_EVENT_COLORS.length];
}

function formatDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function toCalendarDate(dateStr: string): CalendarDate | null {
  if (!dateStr) return null;
  try {
    return parseDate(dateStr);
  } catch {
    return null;
  }
}

function toCalendarDateTime(dateStr: string, timeStr: string): CalendarDateTime | null {
  const cd = toCalendarDate(dateStr);
  if (!cd) return null;
  if (!timeStr) return new CalendarDateTime(cd.year, cd.month, cd.day, 0, 0);
  // Parse time like "6:00 PM", "18:00", "6 PM", etc.
  const match = timeStr.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)?$/i);
  if (!match) return new CalendarDateTime(cd.year, cd.month, cd.day, 0, 0);
  let hour = parseInt(match[1]);
  const minute = match[2] ? parseInt(match[2]) : 0;
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === "PM" && hour < 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;
  return new CalendarDateTime(cd.year, cd.month, cd.day, hour, minute);
}

function calendarDateTimeToDateStr(dt: CalendarDateTime): string {
  return formatDateStr(dt.year, dt.month, dt.day);
}

function calendarDateTimeToTimeStr(dt: CalendarDateTime): string {
  const h = dt.hour;
  const m = dt.minute;
  const meridiem = h >= 12 ? "PM" : "AM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, "0")} ${meridiem}`;
}

const TIME_OPTIONS = [
  "12:00 AM", "1:00 AM", "2:00 AM", "3:00 AM", "4:00 AM", "5:00 AM",
  "6:00 AM", "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
  "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM", "11:00 PM",
];

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return formatDateStr(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function getDaysBetween(startStr: string, endStr: string): string[] {
  const days: string[] = [];
  let current = startStr;
  while (current <= endStr) {
    days.push(current);
    current = addDays(current, 1);
  }
  return days;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type CalendarView = "month" | "week" | "day";

const SubEventManager = forwardRef<SubEventManagerHandle, SubEventManagerProps>(({ defaultValue }, ref) => {
  const hasDefault = Array.isArray(defaultValue) && defaultValue.length > 0;
  const [subEvents, setSubEvents] = useState<SubEvent[]>(hasDefault ? defaultValue : []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const hiddenRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formStartDate, setFormStartDate] = useState("");
  const [formEndDate, setFormEndDate] = useState("");
  const [formStartTime, setFormStartTime] = useState("5:00 AM");
  const [formEndTime, setFormEndTime] = useState("5:00 AM");

  // Expose uncommitted data check to parent
  useImperativeHandle(ref, () => ({
    hasUncommittedData: () => {
      return !!(formName.trim() || formStartDate || formEndDate);
    },
  }));

  // Key to force DatePicker remount when dates change from calendar clicks
  const [formKey, setFormKey] = useState(0);

  // List pagination, search & past events toggle
  const [showPast, setShowPast] = useState(false);
  const [listPage, setListPage] = useState(0);
  const [listSearch, setListSearch] = useState("");
  const ITEMS_PER_PAGE = 8;

  // Calendar state
  const todayDate = today(getLocalTimeZone());
  const todayStr = formatDateStr(todayDate.year, todayDate.month, todayDate.day);
  const [calendarMonth, setCalendarMonth] = useState(todayDate.month);
  const [calendarYear, setCalendarYear] = useState(todayDate.year);
  const [calendarView, setCalendarView] = useState<CalendarView>("month");

  // For week/day view, track the focused date
  const [focusedDate, setFocusedDate] = useState(todayStr);

  // Interactive selection: "idle" = next click sets start, "selecting" = next click sets end
  const [selectionPhase, setSelectionPhase] = useState<"idle" | "selecting">("idle");
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const serializedValue = subEvents.length > 0 ? JSON.stringify(subEvents) : "";

  const resetForm = () => {
    setFormName("");
    setFormStartDate("");
    setFormEndDate("");
    setFormStartTime("5:00 AM");
    setFormEndTime("5:00 AM");
    setEditingId(null);
    setSelectionPhase("idle");
    setHoveredDate(null);
    setFormKey((k) => k + 1);
  };

  const handleAdd = () => {
    if (!formName.trim() || !formStartDate || !formEndDate) return;
    const newSubEvent: SubEvent = {
      id: generateId(),
      name: formName.trim(),
      start_date: formStartDate,
      end_date: formEndDate,
      start_time: formStartTime,
      end_time: formEndTime,
    };
    setSubEvents((prev) => [...prev, newSubEvent]);
    resetForm();
  };

  const handleSave = () => {
    if (!editingId || !formName.trim() || !formStartDate || !formEndDate) return;
    setSubEvents((prev) =>
      prev.map((se) =>
        se.id === editingId
          ? {
              ...se,
              name: formName.trim(),
              start_date: formStartDate,
              end_date: formEndDate,
              start_time: formStartTime,
              end_time: formEndTime,
            }
          : se
      )
    );
    resetForm();
  };

  const handleEdit = (se: SubEvent) => {
    setEditingId(se.id);
    setFormName(se.name);
    setFormStartDate(se.start_date);
    setFormEndDate(se.end_date);
    setFormStartTime(se.start_time);
    setFormEndTime(se.end_time);
    setSelectionPhase("idle");
    setHoveredDate(null);
    setFormKey((k) => k + 1);
  };

  const handleDelete = (id: string) => {
    setSubEvents((prev) => prev.filter((se) => se.id !== id));
    if (editingId === id) resetForm();
  };

  // Interactive calendar click - populates form date inputs
  const handleDayClick = (dateStr: string) => {
    if (selectionPhase === "idle") {
      // First click: set start date
      setFormStartDate(dateStr);
      setFormEndDate("");
      setSelectionPhase("selecting");
      setFormKey((k) => k + 1);
    } else {
      // Second click: set end date (swap if needed)
      let start = formStartDate;
      let end = dateStr;
      if (end < start) [start, end] = [end, start];
      setFormStartDate(start);
      setFormEndDate(end);
      setSelectionPhase("idle");
      setHoveredDate(null);
      setFormKey((k) => k + 1);
    }
  };

  // Get the sub-event color for a given date string
  const getSubEventForDate = (dateStr: string): { idx: number; se: SubEvent } | null => {
    for (let idx = 0; idx < subEvents.length; idx++) {
      const se = subEvents[idx];
      if (se.id === editingId) continue;
      if (se.start_date && se.end_date && dateStr >= se.start_date && dateStr <= se.end_date) {
        return { idx, se };
      }
    }
    return null;
  };

  // Get day cell styling
  const getDayStyle = (dateStr: string) => {
    // Active selection range (form start/end or hover preview)
    const effectiveEnd = selectionPhase === "selecting" && hoveredDate
      ? hoveredDate
      : formEndDate;

    if (formStartDate && effectiveEnd) {
      const rangeStart = formStartDate < effectiveEnd ? formStartDate : effectiveEnd;
      const rangeEnd = formStartDate < effectiveEnd ? effectiveEnd : formStartDate;
      if (dateStr >= rangeStart && dateStr <= rangeEnd) {
        if (dateStr === rangeStart || dateStr === rangeEnd) {
          return "bg-primary-600 text-white font-semibold";
        }
        return "bg-primary-100 text-primary-700 font-medium";
      }
    } else if (formStartDate && dateStr === formStartDate) {
      return "bg-primary-600 text-white font-semibold";
    }

    // Existing sub-events
    const match = getSubEventForDate(dateStr);
    if (match) {
      const color = getColor(match.idx);
      return `${color.bg} ${color.text} font-semibold`;
    }

    // Today
    if (dateStr === todayStr) {
      return "text-primary-600 font-semibold ring-1 ring-primary-300";
    }

    return "text-gray-700 hover:bg-gray-100";
  };

  // Navigation helpers
  const prevPeriod = () => {
    if (calendarView === "month") {
      if (calendarMonth === 1) { setCalendarMonth(12); setCalendarYear(calendarYear - 1); }
      else setCalendarMonth(calendarMonth - 1);
    } else if (calendarView === "week") {
      setFocusedDate(addDays(focusedDate, -7));
    } else {
      setFocusedDate(addDays(focusedDate, -1));
    }
  };

  const nextPeriod = () => {
    if (calendarView === "month") {
      if (calendarMonth === 12) { setCalendarMonth(1); setCalendarYear(calendarYear + 1); }
      else setCalendarMonth(calendarMonth + 1);
    } else if (calendarView === "week") {
      setFocusedDate(addDays(focusedDate, 7));
    } else {
      setFocusedDate(addDays(focusedDate, 1));
    }
  };

  const goToToday = () => {
    setCalendarMonth(todayDate.month);
    setCalendarYear(todayDate.year);
    setFocusedDate(todayStr);
  };

  // Build month grid
  const calendarDate = new CalendarDate(calendarYear, calendarMonth, 1);
  const monthStart = startOfMonth(calendarDate);
  const monthEnd = endOfMonth(calendarDate);
  const firstDayOfWeek = getDayOfWeek(monthStart, "en-US");
  const daysInMonth = monthEnd.day;

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) currentWeek.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  // Build week view dates
  const getWeekDates = (): string[] => {
    const d = new Date(focusedDate + "T00:00:00");
    const dayOfWeek = d.getDay();
    const weekStart = addDays(focusedDate, -dayOfWeek);
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  // Get sub-events for a specific date (for week/day detail view)
  const getSubEventsForDate = (dateStr: string): { se: SubEvent; idx: number }[] => {
    const results: { se: SubEvent; idx: number }[] = [];
    subEvents.forEach((se, idx) => {
      if (se.start_date && se.end_date && dateStr >= se.start_date && dateStr <= se.end_date) {
        results.push({ se, idx });
      }
    });
    return results;
  };

  // Header label
  const getHeaderLabel = () => {
    if (calendarView === "month") {
      return `${MONTH_NAMES[calendarMonth - 1]} ${calendarYear}`;
    }
    if (calendarView === "week") {
      const weekDates = getWeekDates();
      const startParts = weekDates[0].split("-");
      const endParts = weekDates[6].split("-");
      return `${MONTH_NAMES[parseInt(startParts[1]) - 1]} ${parseInt(startParts[2])} - ${MONTH_NAMES[parseInt(endParts[1]) - 1]} ${parseInt(endParts[2])}, ${endParts[0]}`;
    }
    const parts = focusedDate.split("-");
    const d = new Date(focusedDate + "T00:00:00");
    return `${WEEKDAYS_FULL[d.getDay()]}, ${MONTH_NAMES[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
  };

  // Selection hint
  const selectionHint = selectionPhase === "idle"
    ? "Click a date to set start"
    : "Click another date to set end";

  const viewButtonClass = (view: CalendarView) =>
    `px-3 py-1 text-xs font-medium rounded-md transition-colors ${
      calendarView === view
        ? "bg-primary-600 text-white"
        : "text-gray-600 hover:bg-gray-100"
    }`;

  return (
    <div className="col-span-6">
      <div className="grid grid-cols-6 gap-6">
        {/* Left column: Calendar + Form */}
        <div className="col-span-4 space-y-6">
        {/* Calendar */}
        <div>
          <div className="bg-white shadow sm:rounded-md px-4 py-5 sm:p-6">
            {/* View toggles + navigation */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button type="button" onClick={prevPeriod} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button type="button" onClick={nextPeriod} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-gray-700 ml-2">{getHeaderLabel()}</span>
              </div>
              <div className="flex items-center gap-1">
<button type="button" onClick={() => setCalendarView("day")} className={viewButtonClass("day")}>Day</button>
                <button type="button" onClick={() => setCalendarView("week")} className={viewButtonClass("week")}>Week</button>
                <button type="button" onClick={() => setCalendarView("month")} className={viewButtonClass("month")}>Month</button>
              </div>
            </div>

            {/* Selection hint */}
            <div className="text-center text-xs text-gray-400 mb-2">{selectionHint}</div>

            {/* Month view */}
            {calendarView === "month" && (
              <>
                <div className="grid grid-cols-7 gap-0 mb-1">
                  {WEEKDAYS.map((wd) => (
                    <div key={wd} className="text-center text-xs font-medium text-gray-500 py-1">{wd}</div>
                  ))}
                </div>
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-0">
                    {week.map((day, di) => {
                      if (day === null) return <div key={di} className="h-10" />;
                      const dateStr = formatDateStr(calendarYear, calendarMonth, day);
                      return (
                        <button
                          key={di}
                          type="button"
                          onClick={() => handleDayClick(dateStr)}
                          onMouseEnter={() => { if (selectionPhase === "selecting") setHoveredDate(dateStr); }}
                          onMouseLeave={() => { if (selectionPhase === "selecting") setHoveredDate(null); }}
                          className={`h-10 w-full text-sm rounded-md flex items-center justify-center transition-colors cursor-pointer ${getDayStyle(dateStr)}`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            )}

            {/* Week view */}
            {calendarView === "week" && (
              <div className="grid grid-cols-7 gap-1">
                {getWeekDates().map((dateStr) => {
                  const parts = dateStr.split("-");
                  const dayNum = parseInt(parts[2]);
                  const d = new Date(dateStr + "T00:00:00");
                  const dayName = WEEKDAYS[d.getDay()];
                  const matches = getSubEventsForDate(dateStr);
                  return (
                    <div key={dateStr} className="flex flex-col">
                      <div className="text-center text-xs font-medium text-gray-500 mb-1">{dayName}</div>
                      <button
                        type="button"
                        onClick={() => handleDayClick(dateStr)}
                        onMouseEnter={() => { if (selectionPhase === "selecting") setHoveredDate(dateStr); }}
                        onMouseLeave={() => { if (selectionPhase === "selecting") setHoveredDate(null); }}
                        className={`h-20 w-full text-sm rounded-md flex flex-col items-center justify-start pt-2 transition-colors cursor-pointer ${getDayStyle(dateStr)}`}
                      >
                        <span className="font-semibold">{dayNum}</span>
                        <div className="mt-1 space-y-0.5 w-full px-1">
                          {matches.map(({ se, idx }) => {
                            const color = getColor(idx);
                            return (
                              <div key={se.id} className={`text-[10px] truncate rounded px-1 ${color.bg} ${color.text}`}>
                                {se.name}
                              </div>
                            );
                          })}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Day view */}
            {calendarView === "day" && (() => {
              const matches = getSubEventsForDate(focusedDate);
              return (
                <div>
                  <button
                    type="button"
                    onClick={() => handleDayClick(focusedDate)}
                    onMouseEnter={() => { if (selectionPhase === "selecting") setHoveredDate(focusedDate); }}
                    onMouseLeave={() => { if (selectionPhase === "selecting") setHoveredDate(null); }}
                    className={`w-full h-16 text-lg rounded-md flex items-center justify-center transition-colors cursor-pointer mb-3 ${getDayStyle(focusedDate)}`}
                  >
                    {(() => { const parts = focusedDate.split("-"); return parseInt(parts[2]); })()}
                  </button>
                  {matches.length > 0 ? (
                    <div className="space-y-2">
                      {matches.map(({ se, idx }) => {
                        const color = getColor(idx);
                        return (
                          <button
                            key={se.id}
                            type="button"
                            onClick={() => handleEdit(se)}
                            className={`w-full text-left rounded-md px-3 py-2 ${color.bg} ${color.text} text-sm hover:opacity-80 transition-opacity`}
                          >
                            <div className="font-semibold">{se.name}</div>
                            <div className="text-xs mt-0.5 opacity-75">
                              {se.start_date} {se.start_time} - {se.end_date} {se.end_time}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 text-center py-4">No sub-events on this day</div>
                  )}
                </div>
              );
            })()}

            {/* Legend */}
            {subEvents.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-3">
                  {subEvents.map((se, idx) => {
                    const color = getColor(idx);
                    return (
                      <button
                        key={se.id}
                        type="button"
                        onClick={() => handleEdit(se)}
                        className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                      >
                        <div className={`w-3 h-3 rounded ${color.bg}`} />
                        <span className="text-xs text-gray-600">{se.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input fields - below calendar */}
        <div key={formKey}>
          <div className="bg-white shadow sm:rounded-md px-4 py-5 sm:p-6">
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-4">
                <Input
                  label="Name"
                  name=""
                  defaultValue={formName}
                  onChange={(v) => setFormName(String(v ?? ""))}
                  placeholder="e.g. Show 1"
                />
              </div>
              <div className="col-span-2">
                <DatePicker
                  label="Start Date"
                  defaultValue={toCalendarDate(formStartDate)}
                  onChange={(val: CalendarDate | null) => {
                    if (val) {
                      setFormStartDate(formatDateStr(val.year, val.month, val.day));
                    }
                  }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                <select
                  value={formStartTime}
                  onChange={(e) => setFormStartTime(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Select time</option>
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <DatePicker
                  label="End Date"
                  defaultValue={toCalendarDate(formEndDate)}
                  onChange={(val: CalendarDate | null) => {
                    if (val) {
                      setFormEndDate(formatDateStr(val.year, val.month, val.day));
                    }
                  }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <select
                  value={formEndTime}
                  onChange={(e) => setFormEndTime(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Select time</option>
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-4">
                <div className="flex items-center gap-3">
                  {editingId ? (
                    <>
                      <button
                        type="button"
                        onClick={handleSave}
                        className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={!formName.trim() || !formStartDate || !formEndDate}
                      className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      + Add Sub-Event
                    </button>
                  )}
                  {!editingId && (!formName.trim() || !formStartDate || !formEndDate) && (formName || formStartDate || formEndDate) && (
                    <span className="text-xs text-gray-400">
                      {!formName.trim() ? "Name required" : !formStartDate ? "Start date required" : "End date required"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Sub-event list - 2 cols */}
        <div className="col-span-2">
          {(() => {
            // Sort by start_date descending (most recent first)
            const sorted = [...subEvents].sort((a, b) => b.start_date.localeCompare(a.start_date));
            const searchLower = listSearch.toLowerCase();
            const filtered = searchLower ? sorted.filter((se) => se.name.toLowerCase().includes(searchLower)) : sorted;
            const currentEvents = filtered.filter((se) => se.end_date >= todayStr);
            const pastEvents = filtered.filter((se) => se.end_date < todayStr);
            const displayEvents = (showPast || searchLower) ? [...currentEvents, ...pastEvents] : currentEvents;
            const totalPages = Math.max(1, Math.ceil(displayEvents.length / ITEMS_PER_PAGE));
            const safePage = Math.min(listPage, totalPages - 1);
            const pageEvents = displayEvents.slice(safePage * ITEMS_PER_PAGE, (safePage + 1) * ITEMS_PER_PAGE);
            // Find the index where past events start in the current page
            const pastStartIdx = showPast ? currentEvents.length : -1;

            return (
              <div className="bg-white shadow sm:rounded-md flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-700 flex-shrink-0">Sub-Events</span>
                  <input
                    type="text"
                    value={listSearch}
                    onChange={(e) => { setListSearch(e.target.value); setListPage(0); }}
                    placeholder="Search..."
                    className="flex-1 min-w-0 text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400"
                  />
                  {pastEvents.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setShowPast((v) => !v); setListPage(0); }}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium flex-shrink-0"
                    >
                      {showPast ? "Hide Past" : "Show Past"}
                    </button>
                  )}
                </div>

                {/* List */}
                <div className="flex-1 overflow-hidden">
                  {pageEvents.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-gray-400">
                      No sub-events
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {pageEvents.map((se) => {
                        const originalIndex = subEvents.findIndex((s) => s.id === se.id);
                        const color = getColor(originalIndex);
                        const isPast = se.end_date < todayStr;
                        // Show "Past" divider before the first past event on the page
                        const globalIdx = displayEvents.indexOf(se);
                        const showDivider = showPast && globalIdx === pastStartIdx;

                        return (
                          <div key={se.id}>
                            {showDivider && (
                              <div className="px-4 py-1.5 bg-gray-50 text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-200">
                                Past
                              </div>
                            )}
                            <div className={`px-4 py-3 ${editingId === se.id ? "bg-primary-50 ring-2 ring-inset ring-primary-400" : ""} ${isPast ? "opacity-60" : ""}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className={`w-3 h-3 rounded flex-shrink-0 ${color.bg}`} />
                                  <span className="text-sm font-semibold text-gray-700 truncate">{se.name}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button type="button" onClick={() => handleEdit(se)} className="p-1 text-gray-400 hover:text-gray-600" title="Edit">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  <button type="button" onClick={() => handleDelete(se.id)} className="p-1 text-primary-400 hover:text-primary-600" title="Delete">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </div>
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {se.start_date} {se.start_time && `${se.start_time}`} - {se.end_date} {se.end_time && `${se.end_time}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Pagination footer */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setListPage((p) => Math.max(0, p - 1))}
                      disabled={safePage === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="text-xs text-gray-500">{safePage + 1} of {totalPages}</span>
                    <button
                      type="button"
                      onClick={() => setListPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={safePage >= totalPages - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

      </div>

      <input
        ref={hiddenRef}
        type="hidden"
        name="sub_events"
        value={serializedValue}
      />
    </div>
  );
});

export default SubEventManager;
